import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack
} from '@mui/material'
import { WorkspaceStatus } from '@shared/types/workspaces.types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateWorkspaceMutation } from '../../hooks/useMutations/useWorkspacesMutations'
import { useSnackbar } from '../../hooks/useSnackbar'
import { LoaderButton } from '../LoaderButton'

export const ChangeWorkspaceStatusModal = NiceModal.create(
  ({
    companyId,
    workspaceId,
    workspaceName,
    currentStatus
  }: {
    companyId: string
    workspaceId: string
    workspaceName: string
    currentStatus: WorkspaceStatus
  }) => {
    const modal = useModal()
    const { t } = useTranslation()
    const [status, setStatus] = useState<WorkspaceStatus | undefined>(undefined)
    const { showSnackbar } = useSnackbar()

    const updateWorkspaceMutation = useUpdateWorkspaceMutation(
      companyId,
      workspaceId
    )

    const handleSave = () => {
      updateWorkspaceMutation.mutate(
        {
          status
        },
        {
          onSuccess: () => {
            showSnackbar({
              message: t('change_workspace_status_modal.success'),
              severity: 'success'
            })
            modal.remove()
          },
          onError: () => {
            showSnackbar({
              message: t('change_workspace_status_modal.error'),
              severity: 'error'
            })
          }
        }
      )
    }

    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        sx={{
          '& .MuiDialog-paper': {
            width: '37.5rem',
            padding: '1.5rem'
          }
        }}
      >
        <DialogTitle sx={{ padding: '1.5rem', fontWeight: 'bold' }}>
          {t('change_workspace_status_modal.title', { workspaceName })}
        </DialogTitle>
        <DialogContent sx={{ padding: '1.5rem' }}>
          <Stack spacing='1.25rem'>
            <DialogContentText>
              {t('change_workspace_status_modal.subtitle')}
            </DialogContentText>
            <FormControl>
              <InputLabel id='change-workspace-status-label'>
                {t('change_workspace_status_modal.choose')}
              </InputLabel>
              <Select
                value={status || ''}
                labelId='change-workspace-status-label'
                label={t('change_workspace_status_modal.choose')}
                onChange={e => setStatus(e.target.value as WorkspaceStatus)}
                sx={{ width: '32.5rem' }}
                renderValue={selected => {
                  if (selected) {
                    return t(`workspaces.status.${selected.toLowerCase()}`)
                  }
                }}
              >
                {Object.values(WorkspaceStatus)
                  .filter(status => status !== currentStatus)
                  .map(status => (
                    <MenuItem value={status} key={status}>
                      {t(`workspaces.status.${status}`)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ width: '100%', gap: 1 }}>
          <Button
            variant='outlined'
            onClick={() => modal.remove()}
            sx={{
              border: '1px solid rgba(128, 203, 196, 0.74)',
              color: 'primary.main',
              background: 'rgba(178, 223, 219, 0.10)',
              textTransform: 'capitalize',
              flex: 1
            }}
          >
            {t('common.cancel')}
          </Button>
          <LoaderButton
            variant='contained'
            loading={updateWorkspaceMutation.isPending}
            label={t('change_workspace_status_modal.button')}
            sx={{
              textTransform: 'capitalize',
              flex: 1
            }}
            onClick={handleSave}
            disabled={!status || updateWorkspaceMutation.isPending}
          />
        </DialogActions>
      </Dialog>
    )
  }
)
